import type { QuestionMultipleChoiceSchema } from '@repo/schemas/quiz/questionMultipleChoice';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { questionMultipleChoiceSchema } from '@repo/schemas/quiz/questionMultipleChoice';
import { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

export function useQuestionMultipleForm(data?: QuestionMultipleChoiceSchema) {
  const form = useForm<QuestionMultipleChoiceSchema>({
    defaultValues: data,
    mode: 'onChange',
    resolver: standardSchemaResolver(
      questionMultipleChoiceSchema.transform((data) => ({
        ...data,
        points: data.answers.reduce((sum, answer) => sum + (answer.points || 0), 0),
      }))
    ),
  });

  //trigger validation on component mount
  useEffect(() => {
    form.trigger();
  }, []);

  const multipleQuestionAnswers = useFieldArray<QuestionMultipleChoiceSchema>({
    control: form.control,
    name: 'answers',
  });

  const toggleCorrect = (index: number): void => {
    const answers = form.getValues('answers');
    const answer = answers[index];
    if (!answer) {
      return;
    }

    if (answers.length === 2) {
      const nextSelectedCorrect = !answer.correct;
      const updatedAnswers = answers.map((currentAnswer, currentIndex) => ({
        ...currentAnswer,
        correct: currentIndex === index ? nextSelectedCorrect : !nextSelectedCorrect,
        points: currentIndex === index && nextSelectedCorrect ? (currentAnswer.points ?? 1) : undefined,
      }));

      form.setValue('answers', updatedAnswers, {
        shouldValidate: true,
        shouldDirty: true,
      });
      return;
    }

    const newAnswers = answers.map((answer, answerIndex) => {
      if (answerIndex === index) {
        return {
          ...answer,
          correct: !answer.correct,
        };
      }
      return answer;
    });

    if (newAnswers.reduce((sum, answer) => sum + (answer?.correct ? 1 : 0), 0) === newAnswers.length) {
      toast.error('Nie można zaznaczyć wszystkich odpowiedzi jako poprawne', {
        duration: 1500,
        closeButton: false,
        position: 'top-center',
      });
      return;
    }

    if (newAnswers.reduce((sum, answer) => sum + (answer?.correct ? 1 : 0), 0) === 0) {
      toast.error('Co najmniej jedna odpowiedź musi być poprawna', {
        duration: 1500,
        closeButton: false,
        position: 'top-center',
      });
      return;
    }

    multipleQuestionAnswers.update(index, {
      ...answer,
      correct: !answer.correct,
      points: !answer.correct ? 1 : undefined,
    });
  };

  const handleAddAnswer = (): void => {
    multipleQuestionAnswers.append({
      id: uuidv4(),
      content: 'Nowa odpowiedź',
      correct: false,
      points: undefined,
      explanation: undefined,
    });
  };

  const handleRemoveAnswer = (index: number): void => {
    const formAnswers = form.getValues('answers');
    const answer = formAnswers[index];

    const amountOfCorrectAnswers: number = formAnswers.reduce((sum, answer) => sum + (answer?.correct ? 1 : 0), 0);

    if (amountOfCorrectAnswers === 1 && answer?.correct) {
      toast.error('Nie można usunąć poprawnej odpowiedzi, zmień inną odpowiedź na poprawną i spróbuj ponownie', {
        duration: 1500,
        closeButton: false,
        position: 'top-center',
      });
      return;
    }

    multipleQuestionAnswers.remove(index);
  };

  return {
    form,
    multipleQuestionAnswers,
    toggleCorrect,
    handleAddAnswer,
    handleRemoveAnswer,
  };
}
